// Wire types for the portal REST client — split from api.ts to keep both files
// under the project line limit. api.ts re-exports everything here, so consumers
// keep importing from "../lib/api".
export interface OrgSummary {
  id: string;
  name: string;
  created_at: string;
  member_count: number;
  config_count: number;
  /** 'trial' | 'paid' (older servers may omit it → treated as trial). */
  plan?: string;
  trial_ends_at?: string | null;
}
export interface Member {
  user_id: string;
  email: string;
  role: string;
  created_at: string;
  /** true = invited but hasn't set a password yet (account unconfirmed). */
  pending?: boolean;
}
export interface OrgConfigToggle {
  id: string;
  name: string;
  enabled: boolean;
  /** Canonical extraction fields this supplier's config maps (display order). */
  mappedFields: string[];
}
export type ConfigKind = "css" | "json";
export interface DistributorConfig {
  id: string;
  name: string;
  baseUrl: string;
  loginUrl?: string;
  /** CSS selector matching ONLY when logged in (account/username element, logout link). */
  loggedInMarker?: string;
  /** CSS selector matching ONLY when logged out (header login link, in-row "log in for price"). */
  loggedOutMarker?: string;
  searchPageUrl?: string;
  searchInput?: {
    selector: string;
    submit?: "enter" | "form" | "click";
    submitSelector?: string;
  };
  linkRowsToSearch?: boolean;
  asyncResults?: boolean;
  request: {
    method: "GET" | "POST";
    url: string;
    headers?: Record<string, string>;
    body?: string;
  };
  extraction:
    | {
        kind: "css";
        rowSelector: string;
        fields: Record<string, unknown>;
      }
    | {
        kind: "json";
        resultsPath: string;
        fields: Record<string, unknown>;
      };
  locale?: Record<string, unknown>;
  version: number;
  lastVerified?: string;
  notes?: string;
}
/**
 * One org's OWN definition of a supplier config (`GET /org-config-defs`, owner only).
 * Configs are per-org: this row is the real thing a tenant runs, with its true version.
 * `config` is null when the stored JSON no longer parses (surfaced, never hidden).
 */
export interface OrgConfigDef {
  orgId: string;
  orgName: string;
  configId: string;
  version: number;
  updatedAt: string;
  config: DistributorConfig | null;
}

export interface OrgDetail {
  org: {
    id: string;
    name: string;
    created_at: string;
    plan?: string;
    trial_ends_at?: string | null;
  };
  members: Member[];
  configs: OrgConfigToggle[];
}

/** The resolved plan entitlement (PATCH /orgs/:id + /auth/me report it). */
export interface Entitlement {
  plan: "trial" | "paid";
  bulkAllowed: boolean;
  trialEndsAt: string | null;
  expired: boolean;
}
/**
 * A vendor group = a named, curated subset of supplier ids an org's traders use to
 * scope searches. Editable both in the desktop app and here in the portal.
 */
export interface VendorGroup {
  id: string;
  name: string;
  supplierIds: string[];
}
/** The org's stored group set + its CAS version (saves must carry the version). */
export interface OrgGroups {
  groups: VendorGroup[];
  version: number;
}
/** POST /orgs/onboard input — org + admin user (+ optional supplier set) in one call. */
export interface OnboardInput {
  name: string;
  adminEmail: string;
  adminPassword?: string;
  enableAllVendors?: boolean;
}
export interface OnboardResult {
  org: { id: string; name: string; created_at: string };
  user: { id: string; email: string; role: string; pending: boolean };
  invited: boolean;
}
export interface User {
  id: string;
  email: string;
  created_at: string;
  /** 'portal' = console admin; 'app' = org/customer user. */
  role: string;
  /** True = invited but hasn't set their password yet (account unconfirmed). */
  pending?: boolean;
  /** Org memberships (app users only; portal users have none). */
  orgs?: { id?: string; name: string; role: string }[];
}
/** GET /auth/me — the signed-in account's identity + role. */
export interface Me {
  user: { id?: string; email?: string; role: string };
  org: { id: string; name: string; role: string } | null;
  orgAdmin: boolean;
}
export interface SearchEvent {
  id: string;
  org_id: string | null;
  user_id: string | null;
  user_email: string | null;
  part: string;
  kind: string;
  source: string | null;
  result_count: number | null;
  ok_count: number | null;
  duration_ms: number | null;
  results: unknown;
  created_at: string;
}

/**
 * A captured page snapshot — the stored HTML of a supplier landing/results page as
 * it rendered in a user's logged-in session, with a fast login-state verdict so the
 * owner can eyeball whether the session was truly authenticated. List rows omit `html`;
 * `getSnapshot(id)` returns the full row including it.
 */
export interface PageSnapshot {
  id: string;
  org_id: string | null;
  user_id: string | null;
  user_email: string | null;
  vendor_id: string;
  vendor_name: string | null;
  page_kind: "landing" | "results-in" | "results-out" | "login";
  url: string;
  /** The search term that produced the page. Null on rows captured before it was stored. */
  part?: string | null;
  login_state: "in" | "out" | "unknown" | null;
  markers_note: string | null;
  created_at: string;
  /** Full stored HTML — present only on getSnapshot(id), never in list rows. */
  html?: string;
}

export type ReleaseOs = "windows" | "mac" | "linux";
/** 'held' = uploaded by a release but NOT offered to anyone yet (awaiting promotion). */
export type ReleaseState = "active" | "held" | "previous";
/** One promotion: a staged build being offered to customers, and what justified it. */
export interface ReleasePromotion {
  id: string;
  version: string;
  /** The Chrome Web Store extension version live when this was promoted. */
  store_ext_version: string | null;
  actor: string | null;
  note: string | null;
  created_at: string;
}
/** GET /desktop-releases — the desktop update feed state (for the releases page). */
export interface ReleaseFeed {
  /** Active feed version per platform (from the live latest*.yml), or null. */
  active: Record<ReleaseOs, string | null>;
  /** The version the feed serves (windows first, then mac, then linux), or null. */
  activeVersion: string | null;
  /** Versions still on the volume (newest first) + the platforms each can serve. */
  versions: { version: string; platforms: ReleaseOs[]; state: ReleaseState }[];
  /** Staged-but-withheld versions, newest first. `held[0]` is the promotion candidate. */
  held: string[];
  /** Promotion audit trail, newest first. */
  promotions: ReleasePromotion[];
  /** Last recorded live Chrome Web Store extension version (from the promotion log). */
  storeExtVersion: string | null;
}
/** POST /desktop-releases/activate result — which manifests were rewritten. */
export interface ActivateReleaseResult {
  ok: boolean;
  version: string;
  updated: string[];
  skipped: { os: ReleaseOs; reason: string }[];
  promotion?: ReleasePromotion;
  error?: string;
}


export const PROSPECT_STATUSES = ["new", "to_call", "called", "interested", "demo", "customer", "not_interested"] as const;
export type ProspectStatus = (typeof PROSPECT_STATUSES)[number];
export interface Prospect {
  cui: string;
  name: string;
  caen: string;
  main_caen: string | null;
  county: string | null;
  city: string | null;
  address: string | null;
  phone: string | null;
  website: string | null;
  vat: number | null;
  active: number;
  score: number;
  status: ProspectStatus;
  notes: string | null;
  next_follow_up: string | null;
  owner: string | null;
  last_synced_at: string | null;
}
export interface ProspectQuery {
  status?: string;
  county?: string;
  q?: string;
  due?: string;
  page?: number;
  pageSize?: number;
}
export interface ProspectPage {
  prospects: Prospect[];
  total: number;
  page: number;
  pageSize: number;
  counties: string[];
}
export type ProspectPatch = Partial<Pick<Prospect, "status" | "notes" | "next_follow_up" | "owner">>;
